import { useSensors, useSensor, PointerSensor, KeyboardSensor, type DragEndEvent, DndContext, closestCenter } from "@dnd-kit/core";
import { sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { notificacionTipo, notificacion } from "@/components/Alerts";
import { Breadcrumb } from "@/components/Breadcrumb";
import { IndeterminateCheckbox } from "@/components/IndeterminateCheckbox";
import { Loading } from "@/components/Loading/Loading";
import { HorizontalTabs } from "@/components/Tabs";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import type { PrintData, ResPrintOF, ResOrderPuesto, ArticuloStock, RespuestaFases, RespuestaOrden } from "./GestionFases";
import { type FaseWithID, SortableRow } from "./RowFases";

export function TablaFases(props: { readonly idPuesto: string; }) {
	const queryClient = useQueryClient();
	const [local, setLocal] = useLocalStorage<Record<string, PrintData | null>>("impresiones", {});
	const { data: fasesData, isPending: fasesPending, error: fasesError } = useFases(props.idPuesto);
	const { data: ordenData, isPending: ordenPending, error: ordenError } = useOrden(props.idPuesto);
	const [items, setItems] = useState<FaseWithID[]>([]);
	const { data: stocks, fetchStatus } = useStock(items);
	const [dirty, setDirty] = useState(false);
	const [sorting, setSorting] = useState(true);
	const [selection, setSelection] = useState<string[]>([]);
	const [vista, setVista] = useState<string>("Fase");	//Tabs is not strongly typed, so we use string

	useEffect(() => {
		if (fasesData && ordenData) {
			const mapaOrden = new Map<string, number>();
			for (const orden of ordenData.orden) {
				mapaOrden.set(`${orden.idOF}-${orden.faseOF}`, orden.orden);
			}

			const mapped = fasesData.fases.map(fase => ({ ...fase, id: `${fase.idOF}-${fase.idFase}` }));
			const sorted = mapped.sort((itemA, itemB) => {
				const ordenA = mapaOrden.get(`${itemA.idOF}-${itemA.idFase}`) ?? itemA.idOF;
				const ordenB = mapaOrden.get(`${itemB.idOF}-${itemB.idFase}`) ?? itemB.idOF;
				return ordenA - ordenB;
			});

			//TODO Creo que habria que volver toda esta construccion un hook propio
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setItems(sorted);
		}
	}, [fasesData, ordenData]);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event;
		if (!over) return;

		if (active.id !== over.id) {
			setDirty(true);
			setItems((oldItems) => {
				const oldIndex = oldItems.findIndex(item => item.id === active.id);
				const newIndex = oldItems.findIndex(item => item.id === over.id);

				return arrayMove(oldItems, oldIndex, newIndex);
			});
		}
	}

	const onSetPrinting = () => {
		if (dirty) {
			notificacionTipo("error", "No se puede imprimir cuando hay cambios en el orden sin guardar.");
		} else {
			setSorting(false);
			setSelection(items.map((it) => it.id).filter((id) => !(id in local)));
		}
	};

	const onImprimirOFs = () => {
		const endpoint = "/api/v1/printOrdenesFabricacion";
		const idsToPrint = items.filter((item) => selection.includes(item.id)).map((item) => (item.idOF));
		void sendAjaxRequest<ResPrintOF>("POST", endpoint, { idsToPrint }).then((res) => {
			if (res.status != "success") return notificacionTipo("error", res.message);

			const updated = updateLastPrinted(local, selection);
			setLocal(updated);
			setSorting(true);
			window.open(res.printPDF, "_blank");
		});
	};

	const onImprimirFases = () => {
		const endpoint = "/api/v1/printFasesOF";
		const idsToPrint = items.filter((item) => selection.includes(item.id)).map((item) => ({ idOF: item.idOF, idFase: item.idFase }));

		void sendAjaxRequest<ResPrintOF>("POST", endpoint, { idsToPrint }).then((res) => {
			if (res.status != "success") return notificacionTipo("error", res.message);

			const updated = updateLastPrinted(local, selection);
			setLocal(updated);
			setSorting(true);
			window.open(res.printPDF, "_blank");
		});
	};

	const onGuardarCambios = () => {
		void sendAjaxRequest<ResOrderPuesto>("POST", "/api/v1/setOrderPuesto", {
			idPuesto: props.idPuesto,
			nuevoOrden: items.map((fase, index) => ({ "idOF": fase.idOF, "faseOF": fase.idFase, "idPuesto": props.idPuesto, "orden": index + 1 }))
		}).then((res) => {
			if (res.status != "success") return notificacionTipo("error", res.message);

			setDirty(false);
			notificacion(res.message);
			window.open(res.ordenPDF, "_blank");
			void queryClient.invalidateQueries({ queryKey: ["getOrdenPuesto", props.idPuesto] });
		});
	};


	if (fasesPending || ordenPending) return <Loading />;
	if (fasesError || ordenError) {
		return <div>Error al cargar las fases</div>;
	}

	return (
		<>
			<Breadcrumb
				sectionTitle="Gestión de Puestos"
				links={[
					{ label: "Gestión de Puestos", to: "/gestionfases" },
					{ label: `Puesto ${props.idPuesto}`, active: true }
				]}
			/>

			<div id="fases">
				<HorizontalTabs
					options={[["Fase", "Fase"], [fetchStatus == "idle" ? "Stock" : "Cargando...", "Stock"]]}
					value={vista}
					setValue={fetchStatus == "idle" ? setVista : () => { /* noop */ }} />
			</div>
			<div id="sortable-wrapper" style={{ overflow: "auto", borderBottom: "1px solid #333" }}>
				<table id="sortableFases" style={{ width: "100%", overflow: "clip" }}>
					<thead style={{ position: "sticky", top: "0px", zIndex: "1" }}>
						<HeaderFases
							items={items}
							selection={selection}
							setSelection={setSelection}
							sorting={sorting}
							vista={vista}
						/>
					</thead>
					<tbody className="ui-sortable">
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							accessibility={{ container: document.getElementById("my-a11y-container-id")! }}
						>
							<SortableContext items={items} strategy={verticalListSortingStrategy} >
								{items.map(fase => (
									<SortableRow
										key={fase.id}
										orden={fase}
										selection={selection}
										setSelection={setSelection}
										sorting={sorting}
										print={local[fase.id]}
										stock={stocks?.[fase.idArticulo]}
										vista={vista} />
								))}
							</SortableContext>
						</DndContext>
					</tbody>
				</table>
			</div>

			<div id="my-a11y-container-id" />

			<div style={{ display: "flex", padding: "10px 0", justifyContent: "space-between" }}>
				{sorting ? (
					<div style={{ alignSelf: "flex-start", display: "flex", gap: "10px" }}>
						<button className={dirty ? "btnSecondary" : "btnPrimary"} onClick={onSetPrinting} type="button">
							Imprimir
						</button>
						<button className="btnPrimary" onClick={onGuardarCambios} type="button">
							Guardar cambios
						</button>
					</div>
				) : <>
					<div style={{ alignSelf: "flex-start", display: "flex", gap: "10px" }}>
						<button className="btnSecondary" onClick={() => { setSorting(true); }} type="button">
							Cancelar
						</button>
					</div>

					<div style={{ alignSelf: "flex-end", display: "flex", gap: "10px" }}>
						<button className="btnPrimary" onClick={() => { onImprimirOFs(); }} type="button">
							Imprimir OF
						</button>
						<button className="btnPrimary" onClick={() => { onImprimirFases(); }} type="button">
							Imprimir fases
						</button>
					</div>
				</>}
			</div>
		</>
	);
}

interface HeaderFasesProps {
	readonly items: FaseWithID[];
	readonly selection: string[];
	readonly setSelection: (selection: string[]) => void;
	readonly sorting: boolean;
	readonly vista: string;
}
function HeaderFases(props: HeaderFasesProps) {
	const { items, selection, setSelection, sorting, vista } = props;

	return (
		<tr>
			{sorting ? <th /> : <th onClick={(event) => {
				const check = (event.target as HTMLElement).querySelector("input[type='checkbox']");
				if (check) {
					(check as HTMLElement).click();
				}
			}}>
				<IndeterminateCheckbox
					indeterminate={selection.length > 0 && selection.length < items.length}
					checked={selection.length == items.length}
					onChange={() => {
						if (selection.length == items.length) {
							setSelection([]);
						} else {
							setSelection(items.map((item) => item.id));
						}
					}} />
			</th>}
			<th>OF-Fase</th>
			{vista === "Fase" && <th>Operacion</th>}
			<th>Conjunto</th>
			<th>Cantidad</th>
			{vista === "Fase" ? <>
				<th>Fecha Inicio</th>
				<th>Fecha Fin</th>
				<th>Última Impresión</th>
			</> : <>
				<th>Máximo</th>
				<th>Mínimo</th>
				<th>Pdte. Entrada</th>
				<th>Pdte. Salida</th>
				<th>Existencias</th>
				<th>PEDIR</th>
			</>}
		</tr>
	);
}


function useFases(idPuesto: string) {
	return useQuery({
		queryKey: ["getFasesPuesto", idPuesto],
		queryFn: () => {
			return sendAjaxRequest<RespuestaFases>("GET", "/api/v1/getFasesPuesto", { idPuesto: idPuesto });
		},
	});
}
function useOrden(idPuesto: string) {
	return useQuery({
		queryKey: ["getOrdenPuesto", idPuesto],
		queryFn: () => {
			return sendAjaxRequest<RespuestaOrden>("GET", "/api/v1/getOrdenPuesto", { idPuesto: idPuesto });
		},
	});
}

function useStock(items: FaseWithID[]) {
	const idsArticulos = items.map((item) => item.idArticulo).filter((id, index, arr) => arr.indexOf(id) === index);
	return useQuery<Record<string, ArticuloStock>>({
		queryKey: ["getStockById", idsArticulos.join(",")],
		queryFn: () => {
			return sendAjaxRequest<ArticuloStock[]>("GET", "/api/v1/getArticulosStock", { idsArticulos })
				.then((res) => {
					const stocks: Record<string, ArticuloStock> = {};
					for (const stock of res) {
						stocks[stock.idArticulo] = stock;
					}
					return stocks;
				});
		},
		enabled: items.length > 0
	});
}


function updateLastPrinted(local: Record<string, PrintData | null>, selection: string[]): Record<string, PrintData | null> {
	const updated = { ...local };
	const now = Math.floor(new Date().getTime() / 1000);
	for (const id of selection) {
		if (updated[id] == null) {
			updated[id] = {
				count: 1,
				last: now
			};
		} else if (typeof updated[id] === "number") {
			// Inicialmente, localStorage guardaba únicamente el número de impresiones totales
			updated[id] = {
				count: (updated[id] as number) + 1,
				last: now
			};
		} else {
			updated[id] = {
				count: updated[id].count + 1,
				last: now
			};
		}
	}

	return updated;
}